import { Resource,ResourceLoadContext } from "./resource";

export interface TextureAsset {
  texture: GPUTexture;
  view: GPUTextureView;
  sampler: GPUSampler;
  width: number;
  height: number;
  format: GPUTextureFormat;
}

export class TextureResource extends Resource<TextureAsset> {
  async load(ctx: ResourceLoadContext): Promise<TextureAsset> {
    const response = await ctx.fetchPrivate(this.key);
    if (!response.ok) {
      throw new Error(`Failed to load texture "${this.key}": ${response.status}`);
    }

    const blob = await response.blob();
    // Private Robot02 white-base PNGs store paint coverage in alpha, not
    // opacity. Retain RGB even where that mask is zero (charcoal/details).
    // Leave all existing scene textures on their unchanged decode path.
    const hasPaintMask = /(?:^|\/)robot02_white_v10_1024(?:_[12])?\.png(?:\?|$)/.test(this.key)
      || /(?:^|\/)robot02_white_v(?:11|12|13|14|15|16|17|18|19|20|21|23|24)_1024(?:_(?:[1-9]|10))?_regions\.png(?:\?|$)/.test(this.key)
      || /(?:^|\/)robot_v2_.*_regions\.png(?:\?|$)/.test(this.key);
    const bitmap = hasPaintMask
      ? await createImageBitmap(blob, { premultiplyAlpha: "none", colorSpaceConversion: "none" })
      : await createImageBitmap(blob);

    const format: GPUTextureFormat = "rgba8unorm";

    const texture = ctx.device.createTexture({
      label: `TextureResource:${this.key}`,
      size: {
        width: bitmap.width,
        height: bitmap.height,
        depthOrArrayLayers: 1,
      },
      format,
      mipLevelCount: 1,
      sampleCount: 1,
      dimension: "2d",
      usage:
        GPUTextureUsage.TEXTURE_BINDING |
        GPUTextureUsage.COPY_DST |
        GPUTextureUsage.RENDER_ATTACHMENT,
    });

    ctx.device.queue.copyExternalImageToTexture(
      { source: bitmap },
      { texture, premultipliedAlpha: false },
      { width: bitmap.width, height: bitmap.height }
    );

    const view = texture.createView({
      label: `TextureResourceView:${this.key}`,
    });

    // Arena strips repeat only horizontally; isolated props must not sample
    // the opposite edge. Preserve the legacy sampler for every other texture.
    const arenaTexture = /(?:^|\/)boneyard_v(?:02a?|04|05)_/.test(this.key);
    const arenaRepeat = /_repeat\.png(?:\?|$)/.test(this.key);
    const sampler = ctx.device.createSampler({
      label: `TextureResourceSampler:${this.key}`,
      magFilter: "linear",
      minFilter: "linear",
      mipmapFilter: "linear",
      addressModeU: arenaTexture && !arenaRepeat ? "clamp-to-edge" : "repeat",
      addressModeV: arenaTexture ? "clamp-to-edge" : "repeat",
    });

    this._value = {
      texture,
      view,
      sampler,
      width: bitmap.width,
      height: bitmap.height,
      format,
    };

    return this._value;
  }

  dispose(_ctx: ResourceLoadContext): void {
    if (this._value) {
      this._value.texture.destroy();
      this._value = undefined;
    }
  }
}
